import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { ISignForm, SignColor, SymbolPlacement } from "../../interfaces/ISignForm";
import { useTranslation } from "react-i18next";
import { mapColorCodeToTranslatedColorText } from "../../helper/signGeneratorHelper";
type Props = {
  order: ISignForm;
  sign: string;
  logo: string;
};
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 24,
  },
  divider: {
    border: "1px solid #5F5E91",
  },
  signPreviewContainer: {
    alignItems: "center",
    width: "100%",
    marginVertical: 10,
  },
  image: {
    maxWidth: 200,
    maxHeight: 200,
    objectFit: "contain",
  },
  logo: {
    width: 100,
  },
  section: {
    marginVertical: 5,
  },
  table: {
    display: "flex",
    flexDirection: "column",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 6,
    borderBottom: "1px solid #5F5E91",
  },
  priceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 6,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    marginTop: 20,
    gap: 20,
  },
  footerLabels: {
    alignItems: "flex-end",
    marginRight: 16,
  },
  prices: {
    alignItems: "flex-end",
    textAlign: "right",
    minWidth: 70,
  },
  totalPrice: {
    fontSize: 14,
    fontWeight: "bold",
  },
  pricePerUnit: {
    fontSize: 12,
  },
  bold: {
    fontWeight: "bold",
  },
  footerText: {
    fontStyle: "italic",
    fontSize: 8,
  },
});
export const SignOrderPDF = ({ order, sign, logo }: Props) => {
  const { t } = useTranslation();
  const texts = order?.texts?.filter((text) => text.text !== "");
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <Text>{t("label.orderConfirmation")}</Text>
        </View>

        <View style={styles.divider}></View>

        <View style={styles.signPreviewContainer}>
          <Image src={sign} style={styles.image} />
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>{t("label.contactInformation")}</Text>
          <View style={styles.table}>
            <View style={styles.row}>
              <Text>{t("label.companyName")}</Text>
              <Text>{order.customer.companyName}</Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.name")}</Text>
              <Text>{order.customer.name}</Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.email")}</Text>
              <Text>{order.customer.email}</Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.phone")}</Text>
              <Text>{order.customer.phone}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>{t("label.design")}</Text>
          <View style={styles.table}>
            <View style={styles.row}>
              <Text>{t("label.width")}</Text>
              <Text>
                {order.width}
                {t("unit.mm")}
              </Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.height")}</Text>
              <Text>
                {order.height}
                {t("unit.mm")}
              </Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.color")}</Text>
              <Text>
                {order.primaryColor === SignColor.BLUE
                  ? t("option.blueWhite")
                  : t("option.yellowRed")}
              </Text>
            </View>
            {order?.symbol && (
              <View style={styles.row}>
                <Text>{t("label.symbol")}</Text>
                <Text>{order?.symbol?.name}</Text>
              </View>
            )}
            {order?.symbol && (
              <View style={styles.row}>
                <Text>{t("label.symbolPlacement")}</Text>
                <Text>
                  {order.symbolPlacement === SymbolPlacement.TOP_LEFT
                    ? t("symbolPlacement.topLeftLabel")
                    : t("symbolPlacement.centerLabel")}
                </Text>
              </View>
            )}
          </View>
        </View>

        {texts?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("label.textRows")}</Text>
            <View style={styles.table}>
              {order?.texts?.map((textRow) => (
                <View style={styles.row} key={textRow.row}>
                  <Text>{`${t("label.row")}  ${textRow.row}`}</Text>
                  {textRow?.text === "" ? (
                    <Text style={styles.bold}>{`${t("label.rowbreak")}`}</Text>
                  ) : (
                    <>
                      <Text>{`${t("label.text")} ${textRow.text}`}</Text>
                      <Text>{`${t(
                        "label.color"
                      )} ${mapColorCodeToTranslatedColorText(
                        textRow.color,
                        t
                      )}`}</Text>
                    </>
                  )}
                  <Text>{`${t("label.size")} ${textRow.fontSize}${t(
                    "unit.mm"
                  )}`}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.bold}>{t("label.additions")}</Text>
          <View style={styles.table}>
            <View style={styles.row}>
              <Text>{t("label.reflex")}</Text>
              <Text>{t(`enum.${order.reflex}`)}</Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.graffitiProtection")}</Text>
              <Text>
                {order.graffitiProtection ? t("badge.yes") : t("badge.no")}
              </Text>
            </View>
            {/* <View style={styles.row}>
              <Text>{t("label.mount")}</Text>
              <Text>{t(`enum.${order.mount}`)}</Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.poleDimension2")}</Text>
              <Text>{`${order.poleDimension}${t("unit.mm")}`}</Text>
            </View>
            {order.mount === SignMount.POLE && (
              <View style={styles.row}>
                <Text>{t("label.typeOfMounting")}</Text>
                <Text>{t(`select.${order.mountingPoint}`)}</Text>
              </View>
            )}
            <View style={styles.row}>
              <Text>{t("label.amountOfClamps2")}</Text>
              <Text>{`${order.mountTotal}${t("unit.st")}`}</Text>
            </View>
            <View style={styles.row}>
              <Text>{t("label.needsMounting")}</Text>
              <Text>
                {order.needsMounting ? t("badge.yes") : t("badge.no")}
              </Text>
            </View> */}
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerLabels}>
            <Text style={styles.pricePerUnit}>{t("label.unitPrice")}</Text>
            <Text style={styles.pricePerUnit}>{t("label.amount")}</Text>
            <Text style={styles.totalPrice}>{t("label.totalPrice")}</Text>
          </View>
          <View style={styles.prices}>
            <Text style={styles.pricePerUnit}>
              {order.quantityPrice} {t("unit.krVat")}
            </Text>
            <Text style={styles.pricePerUnit}>
              {order.quantity} {t("unit.st")}
            </Text>
            <Text style={styles.totalPrice}>
              {order.totalPrice} {t("unit.krVat")}
            </Text>
          </View>
        </View>

        <View>
          <Text style={styles.footerText}>
            {t("generalText.orderConfirmationInfo")}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
